/**
 * File Validation Utilities
 * Security layer for file uploads - validates type, size, and content
 */

// Allowed image MIME types
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

// Allowed image extensions
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];

// Magic bytes for image file types (first few bytes of file)
const IMAGE_MAGIC_BYTES = {
  jpeg: [0xFF, 0xD8, 0xFF],
  png: [0x89, 0x50, 0x4E, 0x47],
  webp: [0x52, 0x49, 0x46, 0x46], // RIFF header (need to also check for WEBP)
  heic: [0x00, 0x00, 0x00], // ftyp box (varies, checking done differently)
};

// Default limits
const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const DEFAULT_MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images

/**
 * Validate file type by extension
 * @param {File} file - File to validate
 * @param {string[]} allowedExtensions - Allowed extensions (with dot)
 * @returns {boolean}
 */
function validateExtension(file, allowedExtensions = ALLOWED_IMAGE_EXTENSIONS) {
  if (!file?.name) return false;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return allowedExtensions.includes(ext);
}

/**
 * Validate file MIME type
 * @param {File} file - File to validate
 * @param {string[]} allowedTypes - Allowed MIME types
 * @returns {boolean}
 */
function validateMimeType(file, allowedTypes = ALLOWED_IMAGE_TYPES) {
  if (!file?.type) return false;
  return allowedTypes.includes(file.type.toLowerCase());
}

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSize - Maximum size in bytes
 * @returns {boolean}
 */
function validateFileSize(file, maxSize = DEFAULT_MAX_IMAGE_SIZE) {
  if (!file?.size) return false;
  return file.size <= maxSize;
}

/**
 * Read first bytes of file to check magic bytes
 * @param {File} file - File to check
 * @param {number} numBytes - Number of bytes to read
 * @returns {Promise<Uint8Array>}
 */
async function readFileHeader(file, numBytes = 12) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(new Uint8Array(e.target.result));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file.slice(0, numBytes));
  });
}

/**
 * Validate file content by checking magic bytes
 * @param {File} file - File to validate
 * @returns {Promise<boolean>}
 */
async function validateImageMagicBytes(file) {
  try {
    const header = await readFileHeader(file, 12);

    // Check JPEG (starts with FF D8 FF)
    if (header[0] === 0xFF && header[1] === 0xD8 && header[2] === 0xFF) {
      return true;
    }

    // Check PNG (starts with 89 50 4E 47)
    if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4E && header[3] === 0x47) {
      return true;
    }

    // Check WebP (RIFF....WEBP)
    if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) {
      // Check for WEBP at position 8-11
      if (header[8] === 0x57 && header[9] === 0x45 && header[10] === 0x42 && header[11] === 0x50) {
        return true;
      }
    }

    // Check HEIC/HEIF (ftyp box - bytes 4-7 should be 'ftyp')
    if (header[4] === 0x66 && header[5] === 0x74 && header[6] === 0x79 && header[7] === 0x70) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Comprehensive image file validation
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Maximum file size in bytes (default: 10MB)
 * @param {boolean} options.checkMagicBytes - Whether to check file content (default: true)
 * @returns {Promise<{valid: boolean, error?: string}>}
 */
export async function validateImageFile(file, options = {}) {
  const {
    maxSize = DEFAULT_MAX_IMAGE_SIZE,
    checkMagicBytes = true,
  } = options;

  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file size
  if (!validateFileSize(file, maxSize)) {
    const maxMB = Math.round(maxSize / 1024 / 1024);
    return { valid: false, error: `File too large. Maximum size is ${maxMB}MB` };
  }

  // Check MIME type
  if (!validateMimeType(file, ALLOWED_IMAGE_TYPES)) {
    return { valid: false, error: 'Invalid file type. Please use JPEG, PNG, or WebP images' };
  }

  // Check extension
  if (!validateExtension(file, ALLOWED_IMAGE_EXTENSIONS)) {
    return { valid: false, error: 'Invalid file extension. Please use .jpg, .jpeg, .png, or .webp' };
  }

  // Check magic bytes (content validation)
  if (checkMagicBytes) {
    const validContent = await validateImageMagicBytes(file);
    if (!validContent) {
      return { valid: false, error: 'File content does not match an image format' };
    }
  }

  return { valid: true };
}

/**
 * Validate multiple image files
 * @param {File[]} files - Files to validate
 * @param {Object} options - Validation options
 * @returns {Promise<{valid: boolean, errors: string[], validFiles: File[]}>}
 */
export async function validateImageFiles(files, options = {}) {
  const errors = [];
  const validFiles = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const result = await validateImageFile(file, options);

    if (result.valid) {
      validFiles.push(file);
    } else {
      errors.push(`File ${i + 1} (${file.name}): ${result.error}`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    validFiles,
  };
}

/**
 * Sanitize filename to prevent path traversal and special characters
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
export function sanitizeFilename(filename) {
  if (!filename) return 'file';

  // Remove path components
  let sanitized = filename.split(/[/\\]/).pop() || 'file';

  // Remove or replace dangerous characters
  sanitized = sanitized
    .replace(/\.\./g, '') // Remove path traversal
    .replace(/[<>:"|?*]/g, '') // Remove Windows forbidden chars
    .replace(/[\x00-\x1f]/g, '') // Remove control characters
    .trim();

  // Ensure it has a valid extension
  if (!sanitized.includes('.')) {
    sanitized += '.jpg';
  }

  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.split('.').pop();
    sanitized = sanitized.substring(0, 250 - ext.length) + '.' + ext;
  }

  return sanitized || 'file.jpg';
}

export {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_IMAGE_EXTENSIONS,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_IMAGE_SIZE,
  validateExtension,
  validateMimeType,
  validateFileSize,
  validateImageMagicBytes,
};
